import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "oidc-react";

export function useGetClasses(courseId: string) {
  const auth = useAuth();

  return useQuery({
    queryKey: ["classes", courseId],
    queryFn: async (): Promise<{
      count: number;
      items: { name: string; id: string }[];
    }> => {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE}/courses/${courseId}/workspaces/groups/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.userData.access_token}`,
          },
        },
      );

      return res.json();
    },
  });
}

export function useGetClass(courseId: string, classId: string | null) {
  const auth = useAuth();

  return useQuery({
    enabled: classId != null,
    queryKey: ["class", classId],
    queryFn: async (): Promise<{
      count: number;
      items: {
        workspace_id: string;
        name: string;
        key: string;
        url: string;
        group_id: string;
      }[];
    }> => {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE}/courses/${courseId}/workspaces/?group=${classId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.userData.access_token}`,
          },
        },
      );

      return res.json();
    },
  });
}

export function useGetStudentProgress(courseId: string, workspaceId: string) {
  const auth = useAuth();

  return useQuery({
    queryKey: ["studentProgress", workspaceId],
    queryFn: async (): Promise<
      {
        module: string;
        lessons: {
          exercise: string;
          no_passed: number;
          no_tests: number;
        }[];
      }[]
    > => {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE}/courses/${courseId}/workspaces/${workspaceId}/progress`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.userData.access_token}`,
          },
        },
      );

      return res.json();
    },
  });
}

export function useCreateClass(courseId: string) {
  const auth = useAuth();
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE}/courses/${courseId}/workspaces/groups/`,
        {
          body: JSON.stringify({ name }),
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.userData.access_token}`,
          },
        },
      );
      return res.json();
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["classes", courseId] });
    },
  });
}

export function useDeleteClass(courseId: string) {
  const auth = useAuth();
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (classId: string) => {
      return await fetch(
        `${import.meta.env.VITE_API_BASE}/courses/${courseId}/workspaces/groups/${classId}/`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.userData.access_token}`,
          },
        },
      );
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["classes", courseId] });
    },
  });
}

export function useCreateStudent(courseId: string, classId: string) {
  const auth = useAuth();
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, email }: { name: string; email?: string }) => {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE}/courses/${courseId}/workspaces/`,
        {
          body: JSON.stringify({ name, email, group_id: classId }),
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.userData.access_token}`,
          },
        },
      );
      return res.json();
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["class", classId] });
    },
  });
}

export function useUpdateStudent(courseId: string, classId: string) {
  const auth = useAuth();
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({
      studentId,
      name,
      email,
    }: {
      studentId: string;
      name: string;
      email?: string;
    }) => {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE}/courses/${courseId}/workspaces/${studentId}/`,
        {
          body: JSON.stringify({ name, email }),
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.userData.access_token}`,
          },
        },
      );
      return res.json();
    },
    onSuccess: (data, variables, context) => {
      client.invalidateQueries({ queryKey: ["class", classId] });
    },
  });
}

export function useDeleteStudent(courseId: string, classId: string) {
  const auth = useAuth();
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ studentId }: { studentId: string }) => {
      return await fetch(
        `${import.meta.env.VITE_API_BASE}/courses/${courseId}/workspaces/${studentId}/`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.userData.access_token}`,
          },
        },
      );
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["class", classId] });
    },
  });
}

export function useSendEmails(courseId: string) {
  const auth = useAuth();

  return useMutation({
    mutationFn: async ({ classId }: { classId: string }) => {
      return await fetch(
        `${import.meta.env.VITE_API_BASE}/courses/${courseId}/workspaces/email/`,
        {
          method: "POST",
          body: JSON.stringify({
            group_id: classId,
          }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.userData.access_token}`,
          },
        },
      );
    },
  });
}
